import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack } from '@mui/material';
import FormInput from '../../inputs/FormInput';
import useGetHotelRoomCategoriesForSelect from '../../../hooks/hotelRoomCategories/useGetHotelRoomCategoriesForSelect';

const AddAssignmentDialog = ({ open, onClose, hotel, onAdd, isArabic }) => {
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [roomCategoryId, setRoomCategoryId] = useState('');
    const [roomsCount, setRoomsCount] = useState(1);
    const [sellPrice, setSellPrice] = useState(0);
    const [costPrice, setCostPrice] = useState(0);

    useEffect(() => {
        if (open) {
            setFromDate(hotel.check_in_date || '');
            setToDate(hotel.check_out_date || '');
            setRoomCategoryId('');
            setRoomsCount(1);
            setSellPrice(0);
            setCostPrice(0);
        }
    }, [open, hotel.check_in_date, hotel.check_out_date]);

    const handleAdd = () => {
        if (!fromDate || !toDate) return;
        if (new Date(toDate) <= new Date(fromDate)) return;
        onAdd({
            from_date: fromDate,
            to_date: toDate,
            room_category_id: roomCategoryId || null,
            rooms_count: parseInt(roomsCount) || 1,
            custom_sell_price: parseFloat(sellPrice) || 0,
            custom_cost_price: parseFloat(costPrice) || 0,
        });
        onClose();
    };

    const { options: roomCategories } = useGetHotelRoomCategoriesForSelect();
    const categoryOptions = useMemo(() => {
        if (!roomCategories) return [];
        return roomCategories.map(item => ({
            id: item.value ?? item.id,
            name: item.label ?? item.name,
        }));
    }, [roomCategories]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
                {isArabic ? 'إضافة توزيع غرف جديد' : 'Add Room Assignment'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <Stack direction="column" spacing={1.5}>
                        <FormInput
                            label={isArabic ? 'من تاريخ' : 'From Date'}
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            min={hotel.check_in_date || ''}
                            max={toDate || hotel.check_out_date || ''}
                            size="small"
                            sx={{ width: '100%' }}
                        />
                        <FormInput
                            label={isArabic ? 'إلى تاريخ' : 'To Date'}
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            min={fromDate || hotel.check_in_date || ''}
                            max={hotel.check_out_date || ''}
                            size="small"
                            sx={{ width: '100%' }}
                        />
                    </Stack>
                    <FormInput
                        label={isArabic ? 'فئة الغرفة' : 'Room Category'}
                        type="select"
                        value={roomCategoryId}
                        onChange={(e) => setRoomCategoryId(e.target.value)}
                        options={categoryOptions}
                        size="small"
                    />
                    <FormInput
                        label={isArabic ? 'عدد الغرف' : 'Rooms Count'}
                        type="number"
                        value={roomsCount}
                        onChange={(e) => {
                            const val = e.target.value;
                            setRoomsCount(val === '' ? '' : parseInt(val));
                        }}
                        inputProps={{ min: 1 }}
                        size="small"
                    />
                    <Stack direction="column" spacing={1}>
                        <FormInput
                            label={isArabic ? 'التكلفة' : 'Cost Price'}
                            type="number"
                            value={costPrice}
                            onChange={(e) => {
                                const val = e.target.value;
                                setCostPrice(val === '' ? '' : parseFloat(val));
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <FormInput
                            label={isArabic ? 'سعر البيع' : 'Sell Price'}
                            type="number"
                            value={sellPrice}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSellPrice(val === '' ? '' : parseFloat(val));
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleAdd}
                    disabled={!fromDate || !toDate || new Date(toDate) <= new Date(fromDate)}
                >
                    {isArabic ? 'إضافة' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddAssignmentDialog;
